'use client';

import { useAuth } from '@clerk/nextjs';
import { ConvexReactClient } from 'convex/react';
import { ConvexProviderWithClerk } from 'convex/react-clerk';
import { type ReactNode, useState } from 'react';

/**
 * Wires the Convex client to Clerk auth so every `useQuery`/`useMutation` carries
 * the Clerk JWT (the token the tenant boundary verifies).
 *
 * Until `NEXT_PUBLIC_CONVEX_URL` is set — i.e. before `npx convex dev` has
 * provisioned a deployment — this is a transparent passthrough: the app renders
 * unchanged and no Convex hook can mount without a provider. Once the URL is set
 * the provider activates with no other change.
 */
export function ConvexClientProvider({
  children,
}: {
  readonly children: ReactNode;
}): React.JSX.Element {
  const [client] = useState<ConvexReactClient | null>(() => {
    const url = process.env['NEXT_PUBLIC_CONVEX_URL'];
    return url === undefined || url === '' ? null : new ConvexReactClient(url);
  });

  if (client === null) return <>{children}</>;
  return (
    <ConvexProviderWithClerk client={client} useAuth={useAuth}>
      {children}
    </ConvexProviderWithClerk>
  );
}
